// src/hooks/useLocationMaster.js
import { useCallback, useEffect, useState } from "react";
import CommonService from "../services/common.service";

export function useLocationMaster() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    async function fetchCountries() {
      try {
        setLoading(true);
        const response = await CommonService.get("/master/company-Country", {}, { silent: true });
        
        if (response && response.success && response.data) {
          // Handle different possible response structures
          const apiCountries = Array.isArray(response.data) 
            ? response.data 
            : response.data.countries || response.data.master_countries || [];
          
          // Map to expected format { id, label }
          const mappedCountries = apiCountries.map((country) => {
            const id = country.country_id || country.id || country.value;
            const label = country.country_name || country.country_label || country.label || country.name || country.country;
            return { id, label };
          }).filter(c => c.id !== undefined && c.id !== null && c.label);
          
          setCountries(mappedCountries);
        }
      } catch (error) {
        console.error("Error fetching countries from API:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchCountries();
  }, []);

  // Fetch states for a country (stable ref — pages use this in useEffect deps)
  const updateListsForCountry = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([]);
      setCities([]);
      return;
    }

    try {
      setLoadingStates(true);
      const response = await CommonService.get(`/master/company-State/${countryId}`, {}, { silent: true });
      
      if (response && response.success && response.data) {
        // Handle different possible response structures
        const apiStates = Array.isArray(response.data) 
          ? response.data 
          : response.data.states || response.data.master_states || [];
        
        // Map to expected format { id, label }
        const mappedStates = apiStates.map((state) => {
          const id = state.state_id || state.id || state.value;
          const label = state.state_name || state.state_label || state.label || state.name || state.state;
          return { id, label };
        }).filter(s => s.id !== undefined && s.id !== null && s.label);
        
        setStates(mappedStates);
        setCities([]); // reset cities when country changes
      }
    } catch (error) {
      console.error("Error fetching states from API:", error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  }, []);

  // Fetch cities for a state
  const updateListsForState = useCallback(async (countryId, stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    try {
      setLoadingCities(true);
      const response = await CommonService.get(`/master/company-City/${stateId}`, {}, { silent: true });
      
      if (response && response.success && response.data) {
        // Handle different possible response structures
        const apiCities = Array.isArray(response.data) 
          ? response.data 
          : response.data.cities || response.data.master_cities || [];
        
        // Map to expected format { id, label }
        const mappedCities = apiCities.map((city) => {
          const id = city.city_id || city.id || city.value;
          const label = city.city_name || city.city_label || city.label || city.name || city.city;
          return { id, label };
        }).filter(c => c.id !== undefined && c.id !== null && c.label);
        
        setCities(mappedCities);
      }
    } catch (error) {
      console.error("Error fetching cities from API:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  return {
    countries,
    states,
    cities,
    loading,
    loadingStates,
    loadingCities,
    updateListsForCountry,
    updateListsForState
  };
}
